import { geocodeByPlaceId, geocodeByAddress } from "react-google-places-autocomplete";

export async function getGeocodeByPlaceId({ isLoaded, placeId }) {
  if (isLoaded) {
    const results = await geocodeByPlaceId(placeId);

    return {
      lat: results[0].geometry.location.lat(),
      lng: results[0].geometry.location.lng(),
    };
  } else {
    console.error(`ERR: Geocoding not loaded yet`);
  }
}

// Extract district/area name from Google Places address components
export function extractAreaName(geocodeResult) {
  if (!geocodeResult || !geocodeResult.address_components) {
    return null;
  }

  const components = geocodeResult.address_components;
  
  // Priority order for area names:
  // 1. sublocality (neighborhood/district)
  // 2. sublocality_level_1 (district)
  // 3. postal_town (town name)
  // 4. locality (city)
  // 5. administrative_area_level_2 (county)
  
  const areaTypes = [
    'sublocality',
    'sublocality_level_1',
    'postal_town',
    'locality',
    'administrative_area_level_2'
  ];

  for (const type of areaTypes) {
    const component = components.find(comp => comp.types.includes(type));
    if (component) {
      return component.long_name;
    }
  }

  return null;
}

export async function getGeocodeWithArea({ isLoaded, placeId }) {
  if (isLoaded) {
    const results = await geocodeByPlaceId(placeId);
    const geocodeResult = results[0];
    
    const areaName = extractAreaName(geocodeResult);
    
    return {
      lat: geocodeResult.geometry.location.lat(),
      lng: geocodeResult.geometry.location.lng(),
      areaName: areaName,
      addressComponents: geocodeResult.address_components,
    };
  } else {
    console.error(`ERR: Geocoding not loaded yet`);
  }
}

export async function getGeocodeByAddress({ address }) {
  const results = await geocodeByAddress(address);

  if (results && results.length > 0) {
    const geocode = results[0];
    return {
      location: {
        lat: geocode?.geometry?.location?.lat(),
        lng: geocode?.geometry?.location?.lng(),
      },
      placeId: geocode?.place_id,
    };
  } else {
    console.error(`Invalid address for geocode lookup!`);
  }
}

// Format address to show only area, city, and postcode (exclude street name)
export function formatAddressWithoutStreet(geocodeResult) {
  if (!geocodeResult || !geocodeResult.address_components) {
    return null;
  }

  const components = geocodeResult.address_components;
  const parts = [];

  // Get area (sublocality, neighborhood, etc.)
  const areaTypes = ['sublocality', 'sublocality_level_1', 'neighborhood'];
  for (const type of areaTypes) {
    const component = components.find(comp => comp.types.includes(type));
    if (component) {
      parts.push(component.long_name);
      break;
    }
  }

  // Get city (locality or postal_town)
  const cityTypes = ['locality', 'postal_town'];
  for (const type of cityTypes) {
    const component = components.find(comp => comp.types.includes(type));
    if (component) {
      parts.push(component.long_name);
      break;
    }
  }

  // Get postcode
  const postcodeComponent = components.find(comp => comp.types.includes('postal_code'));
  if (postcodeComponent) {
    parts.push(postcodeComponent.long_name);
  }

  return parts.filter(Boolean).join(', ') || geocodeResult.formatted_address;
}
